import { IST_TIMEZONE } from '@constants/date';
import { TransactionStatus } from '@features/transactions/constants';
import {
  getPaymentChannelDescription,
  getPaymentModeLabel,
  getPaymentStatusDisplayText,
  quickCollectStatusConfig
} from '@features/transactions/helper';
import { isNonZero } from '@features/transactions/helpers/paymentDetails';
import { MemberTransactionType } from '@types';
import { formatISODate } from '@utils/date';

import DetailRow from './DetailRow';

type TransactionInfoCardProps = {
  memberDetails: MemberTransactionType;
  transactionStatus: TransactionStatus;
  isPaidSuccessView: boolean;
  totalPaid: number;
};

const formatDay = (date: string) =>
  formatISODate({ date, format: 'DD MMM YYYY', timeZone: IST_TIMEZONE });

const formatDayAndTime = (date: string) =>
  formatISODate({
    date,
    format: 'DD MMM YYYY at hh:mm am/pm',
    timeZone: IST_TIMEZONE
  });

const TransactionInfoCard = ({
  memberDetails,
  transactionStatus,
  isPaidSuccessView,
  totalPaid
}: TransactionInfoCardProps) => {
  const statusLabel = getPaymentStatusDisplayText(
    transactionStatus,
    memberDetails.payment_mode
  );
  const statusConfig = quickCollectStatusConfig(transactionStatus, statusLabel);
  const paymentChannelDescription = getPaymentChannelDescription(
    memberDetails.payment_mode
  );
  const paymentMode = getPaymentModeLabel(memberDetails.payment_method);
  const hasPaidAmount = isNonZero(totalPaid);

  const requestedOn = formatDay(memberDetails.requested_on);
  const dueDate = formatDay(memberDetails.due_at);

  return (
    <div className="my-3 flex flex-col gap-2 rounded-lg border border-cofeeGreyBlue4 bg-white p-4">
      {statusLabel &&
        !(!hasPaidAmount && memberDetails.status === 'success') && (
          <DetailRow label="Status" emphasizeLabel>
            <div
              className={`inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ${statusConfig.className}`}
            >
              {statusConfig.label}
            </div>
          </DetailRow>
        )}
      {requestedOn !== dueDate && (
        <DetailRow label="Requested on">{requestedOn}</DetailRow>
      )}
      <DetailRow label="Due date">{dueDate}</DetailRow>
      {isPaidSuccessView && memberDetails.paid_at && hasPaidAmount && (
        <DetailRow label="Paid on">
          {memberDetails.payment_mode === 'automatic'
            ? formatDayAndTime(memberDetails.paid_at)
            : formatDay(memberDetails.paid_at)}
        </DetailRow>
      )}
      {isPaidSuccessView && memberDetails.settled_at && (
        <DetailRow label="Settled on">
          {formatDayAndTime(memberDetails.settled_at)}
        </DetailRow>
      )}
      {isPaidSuccessView && memberDetails.marked_paid_at && (
        <DetailRow label="Marked on">
          {formatDayAndTime(memberDetails.marked_paid_at)}
        </DetailRow>
      )}
      {isPaidSuccessView && memberDetails.utr && (
        <DetailRow label="UTR">{memberDetails.utr}</DetailRow>
      )}
      {isPaidSuccessView && paymentChannelDescription && hasPaidAmount && (
        <DetailRow label="Payment method" wrapValue>
          {paymentChannelDescription}
        </DetailRow>
      )}
      {paymentMode !== '' && (
        <DetailRow label="Payment mode" wrapValue>
          {paymentMode}
        </DetailRow>
      )}
      <DetailRow label="Order ID" emphasizeLabel>
        {memberDetails.id}
      </DetailRow>
      {isPaidSuccessView && memberDetails.payment_description?.trim() && (
        <DetailRow label="Notes" wrapValue>
          {memberDetails.payment_description}
        </DetailRow>
      )}
    </div>
  );
};

export default TransactionInfoCard;
